import React, { useState } from "react";
import { MessageCircle, Mail, ExternalLink } from "lucide-react";
import { DISCORD_LINK, SUPPORT_EMAIL, SOCIAL_LINKS } from "@/constants";

// Custom SVG icons for social media
const YoutubeIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TwitterIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const PatreonIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.386.524c-4.764 0-8.64 3.876-8.64 8.64 0 4.75 3.876 8.613 8.64 8.613 4.75 0 8.614-3.864 8.614-8.613C24 4.4 20.136.524 15.386.524M.003 23.537h4.22V.524H.003"/>
  </svg>
);

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=ICS Contact Form - ${formData.name}&body=${encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    )}`;
    window.location.href = mailtoLink;
  };

  const quickLinks = [
    {
      icon: MessageCircle,
      href: DISCORD_LINK,
      label: "Join Discord",
      description: "Connect with the community",
      external: true,
    },
    {
      icon: Mail,
      href: `mailto:${SUPPORT_EMAIL}`,
      label: "Email Us",
      description: SUPPORT_EMAIL,
      external: false,
    },
    {
      icon: YoutubeIcon,
      href: SOCIAL_LINKS.youtube,
      label: "YouTube",
      description: "Watch our content",
      external: true,
      isSvg: true,
    },
    {
      icon: PatreonIcon,
      href: SOCIAL_LINKS.patreon,
      label: "Patreon",
      description: "Support & Inner Circle",
      external: true,
      isSvg: true,
    },
    {
      icon: FacebookIcon,
      href: SOCIAL_LINKS.facebook,
      label: "Facebook",
      description: "Follow us on Facebook",
      external: true,
      isSvg: true,
    },
    {
      icon: TwitterIcon,
      href: SOCIAL_LINKS.twitter,
      label: "Twitter/X",
      description: "@ICS_MARKETS",
      external: true,
      isSvg: true,
    },
    {
      icon: InstagramIcon,
      href: SOCIAL_LINKS.instagram,
      label: "Instagram",
      description: "@ics_markets",
      external: true,
      isSvg: true,
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-ics-navy-mid"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="section-divider" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-ics-white mb-4">
              Get in Touch
            </h2>
            <p className="text-ics-gray mb-8">
              Have questions about ICS? Reach out and we&apos;ll get back to you.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-ics-white mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input w-full bg-ics-slate/50 border border-white/10 rounded-lg px-4 py-3 text-ics-white placeholder-ics-gray focus:border-ics-orange transition-colors"
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ics-white mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  data-testid="contact-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input w-full bg-ics-slate/50 border border-white/10 rounded-lg px-4 py-3 text-ics-white placeholder-ics-gray focus:border-ics-orange transition-colors"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-ics-white mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  data-testid="contact-message-input"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={5}
                  className="form-input w-full bg-ics-slate/50 border border-white/10 rounded-lg px-4 py-3 text-ics-white placeholder-ics-gray focus:border-ics-orange transition-colors resize-none"
                  placeholder="How can we help?"
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="w-full bg-ics-orange hover:bg-ics-orange-hover text-white font-semibold py-4 rounded-full transition-all duration-300 hover:scale-[1.02]"
              >
                Send Message
              </button>
            </form>
          </div>

          <div className="lg:pt-16">
            <h3 className="font-heading text-xl font-semibold text-ics-white mb-6">
              Quick Links
            </h3>
            <div className="space-y-3">
              {quickLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target={link.external ? "_blank" : undefined}
                  rel={link.external ? "noopener noreferrer" : undefined}
                  data-testid={`contact-link-${link.label.toLowerCase().replace('/', '-')}`}
                  className="flex items-center gap-3 glass-card rounded-lg p-4 hover:bg-white/5 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-ics-orange/20 flex items-center justify-center">
                    {link.isSvg ? (
                      <span className="text-ics-orange">
                        <link.icon />
                      </span>
                    ) : (
                      <link.icon className="w-5 h-5 text-ics-orange" />
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="block text-ics-white font-medium group-hover:text-ics-orange transition-colors">
                      {link.label}
                    </span>
                    <span className="text-sm text-ics-gray">{link.description}</span>
                  </div>
                  {link.external && (
                    <ExternalLink className="w-4 h-4 text-ics-gray" />
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
